import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Copy, Gift, MessageCircle, Share2, Sparkles, Users2,
} from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../components/Toast";

export default function ReferAFriend() {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.myReferrals();
        if (alive) setData(r);
      } catch (e) {
        toast.error(e?.message || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [toast]);

  const code = data?.code || "";
  const link = code ? `${window.location.origin}/register?ref=${code}` : "";
  const bonusMax = data?.bonus_max ?? 5;
  const shareMsg = code
    ? `Hey! Try Mrs CashBack — snap photos of your McDonald's / KFC / Popeyes receipts and earn cash back. Use my code ${code} when you sign up — we both get a bonus: ${link}`
    : "";

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(`${label} copied`); }
      catch { toast.error("Couldn't copy — long-press to copy manually"); }
      document.body.removeChild(ta);
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Mrs CashBack", text: shareMsg, url: link });
      } catch {
        // user cancelled
      }
    } else {
      copy(shareMsg, "Invite message");
    }
  };

  const shareWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareMsg)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 12px 14px",
        borderBottom: "1px solid var(--border)",
        position: "sticky", top: 0, background: "var(--bg)", zIndex: 10,
        paddingTop: "max(16px, env(safe-area-inset-top))",
      }}>
        <button
          data-testid="refer-back-button"
          onClick={() => navigate(-1)}
          style={{
            width: 44, height: 44, borderRadius: 22,
            background: "var(--surface-elev)", border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <ChevronLeft size={22} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 800 }}>Refer a Friend</div>
        <div style={{ width: 44 }} />
      </div>

      <div className="page" style={{ paddingTop: 24 }}>
        {/* Hero card — solid pink with white text */}
        <div style={{
          borderRadius: 28,
          background: "var(--accent-pink)",
          border: "1px solid var(--accent-pink)",
          padding: 22,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 18px 36px -20px rgba(230, 59, 90, 0.55)",
        }}>
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "radial-gradient(circle at 80% -20%, rgba(255,255,255,0.20), transparent 60%)",
          }} />
          <div style={{
            display: "inline-flex", padding: 14, borderRadius: 20,
            background: "rgba(255,255,255,0.22)",
            border: "1px solid rgba(255,255,255,0.35)",
            position: "relative",
          }}>
            <Gift size={32} color="#FFFFFF" />
          </div>
          <h1 style={{
            fontSize: 28, fontWeight: 800, letterSpacing: -1, margin: "16px 0 6px",
            position: "relative", color: "#FFFFFF",
          }}>
            You both earn up to <span style={{ color: "#FFFFFF" }}>£{bonusMax.toFixed(0)}</span>
          </h1>
          <p style={{
            color: "rgba(255,255,255,0.92)", fontSize: 14, lineHeight: 1.5,
            margin: 0, position: "relative",
          }}>
            Share your code. When your friend's <b style={{ color: "#FFFFFF" }}>first receipt</b> is approved, we drop a bonus into both of your wallets.
          </p>
        </div>

        {/* Code card — solid pink with WHITE bg for the code itself, BLACK text */}
        <div style={{
          marginTop: 18, padding: 22,
          background: "var(--accent-pink)", border: "1px solid var(--accent-pink)",
          borderRadius: 28,
          boxShadow: "0 16px 32px -22px rgba(230, 59, 90, 0.5)",
        }}>
          <div className="label" style={{ marginBottom: 10, color: "rgba(255,255,255,0.85)" }}>Your referral code</div>
          {loading ? (
            <div style={{ color: "rgba(255,255,255,0.85)" }}>Loading…</div>
          ) : (
            <>
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "#FFFFFF", border: "1px dashed rgba(0,0,0,0.18)",
                borderRadius: 16, padding: "16px 18px",
              }}>
                <span data-testid="referral-code"
                  style={{
                    flex: 1,
                    color: "#000000",
                    fontSize: 28, fontWeight: 800, letterSpacing: 4,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  {code || "—"}
                </span>
                <button
                  data-testid="copy-referral-code"
                  onClick={() => copy(code, "Code")}
                  style={{
                    padding: "10px 14px",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "var(--accent-pink)", color: "#FFFFFF",
                    border: "none", borderRadius: 999,
                    fontWeight: 800, fontSize: 13,
                  }}
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>

              <div style={{ marginTop: 14 }}>
                <div className="label" style={{ color: "rgba(255,255,255,0.85)" }}>Or share your link</div>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 16, padding: "12px 14px",
                }}>
                  <span data-testid="referral-link"
                    style={{
                      flex: 1,
                      color: "#000000",
                      fontSize: 13, overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {link}
                  </span>
                  <button
                    data-testid="copy-referral-link"
                    onClick={() => copy(link, "Link")}
                    style={{
                      padding: "8px 12px",
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "var(--accent-pink)", color: "#FFFFFF",
                      border: "none", borderRadius: 999,
                      fontWeight: 800, fontSize: 12,
                    }}
                  >
                    <Copy size={14} />
                    Copy
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button
                  data-testid="share-whatsapp"
                  onClick={shareWhatsApp}
                  style={{
                    flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    background: "#25D366", color: "#fff",
                    borderRadius: 999, padding: "14px 16px",
                    fontWeight: 800, fontSize: 14,
                  }}
                >
                  <MessageCircle size={18} />
                  WhatsApp
                </button>
                <button
                  data-testid="share-native"
                  onClick={shareNative}
                  style={{
                    flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "14px 16px",
                    background: "#FFFFFF", color: "var(--accent-pink)",
                    border: "none", borderRadius: 999,
                    fontWeight: 800, fontSize: 14,
                  }}
                >
                  <Share2 size={18} />
                  Share
                </button>
              </div>
            </>
          )}
        </div>

        {/* Stats */}
        {!loading && data && (
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <StatTile
              icon={<Users2 size={18} color="var(--primary)" />}
              label="Sign-ups"
              value={data.total_signups}
              testid="stat-signups"
            />
            <StatTile
              icon={<Sparkles size={18} color="var(--success)" />}
              label="Earned"
              value={`£${(data.earned ?? 0).toFixed(2)}`}
              testid="stat-earned"
            />
          </div>
        )}

        {/* List of referrals */}
        {!loading && data && data.referred_users?.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div className="label" style={{ marginBottom: 8 }}>Your referrals</div>
            {data.referred_users.map((u, idx) => (
              <div
                key={`${u.email}-${idx}`}
                data-testid={`referred-${idx}`}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: "var(--surface)", border: "1px solid var(--border)",
                  borderRadius: 16, padding: 14, marginBottom: 10,
                }}
              >
                <span style={{
                  width: 36, height: 36, borderRadius: 18,
                  background: "linear-gradient(135deg, #F77A2A 0%, #E25C12 100%)", color: "#FFFFFF",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800,
                }}>
                  {(u.name || u.email)[0]?.toUpperCase()}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.name}
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.email}
                  </div>
                </div>
                {u.credited ? (
                  <span className="pill" style={{
                    background: "rgba(46,191,166,0.15)", borderColor: "var(--success)", color: "var(--success)",
                  }}>
                    Earned £{Number(u.bonus_paid || 0).toFixed(2)}
                  </span>
                ) : (
                  <span className="pill" style={{
                    background: "rgba(247,122,42,0.14)", borderColor: "var(--primary)", color: "var(--primary-dark)",
                  }}>
                    Pending
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* How it works */}
        <div className="card" style={{ marginTop: 22, padding: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>How it works</div>
          <Step n="1" body={`Share your code or link with a friend.`} />
          <Step n="2" body="They sign up using your code." />
          <Step n="3" body={`When admin approves their first receipt, you both get a bonus of up to £${bonusMax.toFixed(0)} added to your wallets.`} />
          <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
            One-time bonus per referred user. Bonus is paid only after their first valid receipt is approved.
          </div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="card" style={{ flex: 1, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {icon}
        <span className="label" style={{ margin: 0 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 8 }}>{value}</div>
    </div>
  );
}

function Step({ n, body }) {
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
      <span style={{
        width: 28, height: 28, flexShrink: 0, borderRadius: 14,
        background: "linear-gradient(135deg, #F77A2A 0%, #E25C12 100%)", color: "#FFFFFF",
        fontWeight: 800, fontSize: 13,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 8px 16px -10px rgba(226,92,18,0.55)",
      }}>
        {n}
      </span>
      <div style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5 }}>
        {body}
      </div>
    </div>
  );
}
